// The three ways the Tasks & Approvals page lays out the same open work
// (Sahil, 2026-10-21): By type (a work queue), By urgency and By property.
// Pure functions over what loadOpenWork returns, so the views cannot
// disagree about what is backlog, what is overdue or where an item lives.
//
// An ENTRY is one line on the page, whatever it is:
//   { type: "approval" | "task" | "deadline", id, item, due, backlog, address }
import { TASK_KINDS, PROPERTY_RECORD_LABELS, isBacklog } from "./openWork.js";
import { parseLocalDate, formatLocalDate, propertyLabel, REQUIRED_TENANT_DOCS } from "./helpers.js";
import { CLOCK_KINDS } from "./leaseClockRules.js";

export const TASKS_VIEWS = ["type", "urgency", "property"];
export const TASK_DOC_LABEL = Object.fromEntries(REQUIRED_TENANT_DOCS.map(r => [r.types[0], r.label]));

/** Headings for the By type rail: a kind can sit under Do now and under Backlog. */
const NOW_LABEL = { movein_doc: "Move-in documents", property_record: "Property setup records", lease_ended: "Leases ended", login_missing: "Portal logins missing" };
const BACKLOG_LABEL = { movein_doc: "Move-in documents (older tenancies)", property_record: "Property setup records" };

export function kindLabel(kind, backlog = false) {
  return (backlog ? BACKLOG_LABEL[kind] : NOW_LABEL[kind]) || TASK_KINDS[kind]?.label || kind;
}

/** The few words on a property tile's chip. */
export function chipLabel(entry) {
  const it = entry.item;
  if (entry.type === "approval") return "Approval";
  if (entry.type === "deadline") return CLOCK_KINDS[it.kind]?.label || "Deadline";
  if (it.kind === "movein_doc") return TASK_DOC_LABEL[it.subject] || it.subject || "Document";
  if (it.kind === "property_record") return "Setup: " + (PROPERTY_RECORD_LABELS[it.subject] || it.subject || "record");
  return TASK_KINDS[it.kind]?.label || it.kind;
}

/** Every open line as one entry list: approvals, then tasks, then deadlines. */
export function toEntries(work) {
  const out = [];
  for (const a of work?.approvals || []) out.push({ type: "approval", id: "a:" + a.id, item: a, due: null, backlog: false, address: approvalAddress(a) });
  for (const t of work?.tasks || []) out.push({ type: "task", id: "t:" + t.id, item: t, due: t.dueDate || null, backlog: isBacklog(t), address: TASK_KINDS[t.kind]?.group === "company" ? "" : (t.address || "") });
  const tp = work?.tenantProperty || {};
  for (const d of work?.deadlines || []) out.push({ type: "deadline", id: "d:" + d.item_key, item: d, due: d.due_date || null, backlog: false, address: d.tenant_id != null ? (tp[d.tenant_id] || "") : "" });
  return out;
}

function approvalAddress(a) {
  const r = a?.data || {};
  if (a?.type === "property") return r.address || "";
  if (a?.type === "document") return r.property || "";
  return ""; // a join request is company-wide
}

/** today + n days, as "YYYY-MM-DD" in the viewer's own calendar. */
export function addDays(today, n) {
  const d = parseLocalDate(today);
  d.setDate(d.getDate() + n);
  return formatLocalDate(d);
}

/** "overdue" (before today), "week" (today .. today+7) or "later" (after, or no date). */
export function dueBucket(due, today) {
  if (!due) return "later";
  const d = String(due).slice(0, 10);
  if (d < today) return "overdue";
  if (d <= addDays(today, 7)) return "week";
  return "later";
}

/**
 * By urgency: { decision, overdue, week, later, backlog }, each a list of
 * entries. Approvals are decisions; backlog stays backlog whatever its date
 * (a 2023 tenancy's missing ID is not "overdue" work for this week).
 */
export function urgencyBuckets(entries, today) {
  const out = { decision: [], overdue: [], week: [], later: [], backlog: [] };
  for (const e of entries) {
    if (e.type === "approval") out.decision.push(e);
    else if (e.backlog) out.backlog.push(e);
    else out[dueBucket(e.due, today)].push(e);
  }
  const byDue = (a, b) => String(a.due || "9999").localeCompare(String(b.due || "9999"));
  out.overdue.sort(byDue); out.week.sort(byDue); out.later.sort(byDue);
  return out;
}

/** True when an entry is overdue do-now work (the By property "Only overdue" filter). */
export function isOverdueEntry(e, today) {
  return e.type !== "approval" && !e.backlog && dueBucket(e.due, today) === "overdue";
}

const addrKey = (a) => String(a || "").trim().toLowerCase().replace(/\s+/g, " ");

/** The tenant's name on a tenant line ("Jane Doe: Signed lease" -> "Jane Doe"). */
function tenantName(e) {
  const it = e.item;
  if (e.type === "task" && TASK_KINDS[it.kind]?.group === "tenant") return String(it.title || "").split(":")[0].trim();
  if (e.type === "deadline" && it.tenant_id != null) return String(it.title || "").split(":")[0].trim();
  return "";
}

/**
 * By property: one tile per property, with its tenants' names and its
 * entries. A tenant's line lands on the tenant's property; anything with no
 * property (a company-wide queue, a join request) on the "Company" tile,
 * which comes last. Tiles with high or overdue items first, then by address.
 */
export function groupByProperty(entries) {
  // An address-only line (an approval, a login) joins the tile of the
  // property id that a task with the same address carries.
  const idByAddr = new Map();
  for (const e of entries) if (e.type === "task" && e.item.propertyId != null && e.address) idByAddr.set(addrKey(e.address), e.item.propertyId);
  const tiles = new Map();
  for (const e of entries) {
    const company = !e.address;
    const pid = e.type === "task" && e.item.propertyId != null ? e.item.propertyId : idByAddr.get(addrKey(e.address));
    const key = company ? "company" : pid != null ? "property:" + pid : "address:" + addrKey(e.address);
    if (!tiles.has(key)) tiles.set(key, { key, company, address: company ? "" : e.address, label: company ? "Company" : propertyLabel(e.address) || e.address, tenants: [], entries: [] });
    const tile = tiles.get(key);
    tile.entries.push(e);
    const n = tenantName(e);
    if (n && !tile.tenants.includes(n)) tile.tenants.push(n);
  }
  const hot = (t) => t.entries.some(e => e.item?.severity === "high" || e.item?.severity === "overdue") ? 0 : 1;
  return Array.from(tiles.values()).sort((a, b) => (a.company - b.company) || (hot(a) - hot(b)) || String(a.label).localeCompare(String(b.label)));
}

/**
 * By type: the left rail. { decision, now, backlog }, each a list of
 * { key, label, count }. "approvals" and "deadlines" are their own keys;
 * a task kind is "now:<kind>" or "backlog:<kind>".
 */
export function railEntries(entries) {
  const count = new Map();
  for (const e of entries) {
    const k = entryRailKey(e);
    count.set(k, (count.get(k) || 0) + 1);
  }
  const kinds = (prefix) => Array.from(count.keys()).filter(k => k.startsWith(prefix))
    .map(k => ({ key: k, label: kindLabel(k.slice(prefix.length), prefix === "backlog:"), count: count.get(k) }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  const now = kinds("now:");
  if (count.get("deadlines")) now.push({ key: "deadlines", label: "Deadlines", count: count.get("deadlines") });
  return {
    decision: count.get("approvals") ? [{ key: "approvals", label: "Approvals", count: count.get("approvals") }] : [],
    now,
    backlog: kinds("backlog:"),
  };
}

/** The rail key an entry belongs to. */
export function entryRailKey(e) {
  if (e.type === "approval") return "approvals";
  if (e.type === "deadline") return "deadlines";
  return (e.backlog ? "backlog:" : "now:") + e.item.kind;
}
